import React, { useEffect, useRef } from 'react';

const HeroAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      ctx.scale(dpr, dpr);
    };

    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    // Use lighter, more vibrant colors
    const colors = [
      'rgba(99, 179, 237, 0.8)',  // Lighter blue
      'rgba(104, 211, 145, 0.8)',  // Light green
      'rgba(144, 205, 244, 0.8)',  // Sky blue
      'rgba(190, 227, 248, 0.7)',  // Very light blue
      'rgba(240, 171, 252, 0.7)',  // Light purple
    ];

    // Nodes
    interface Node {
      x: number;
      y: number;
      size: number;
      color: string;
      vx: number;
      vy: number;
      pulse: number;
      pulseDirection: number;
    }

    const nodes: Node[] = [];
    const nodeCount = Math.min(25, Math.floor((canvas.width * canvas.height) / 20000));

    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.clientWidth,
        y: Math.random() * canvas.clientHeight,
        size: Math.random() * 5 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 1,
        vy: (Math.random() - 0.5) * 1,
        pulse: Math.random() * 0.5,
        pulseDirection: Math.random() > 0.5 ? 1 : -1
      });
    }

    // Floating dots
    interface Dot {
      x: number;
      y: number;
      size: number;
      color: string;
      opacity: number;
      speed: number;
    }

    const dots: Dot[] = [];
    const dotCount = Math.min(120, Math.floor((canvas.width * canvas.height) / 8000));
    
    for (let i = 0; i < dotCount; i++) {
      dots.push({
        x: Math.random() * canvas.clientWidth,
        y: Math.random() * canvas.clientHeight,
        size: Math.random() * 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: Math.random() * 0.5 + 0.1,
        speed: Math.random() * 0.5 + 0.1
      });
    }

    // Floating tech symbols
    interface Symbol {
      x: number;
      y: number;
      char: string;
      color: string;
      size: number;
      vx: number;
      vy: number;
      opacity: number;
    }

    const symbols: Symbol[] = [];
    const symbolCount = Math.min(15, Math.floor((canvas.width * canvas.height) / 40000));
    const techChars = ['<>', '/>', '{}', '()', '[]', '&&', '||', '=>', '*', '#', '$', '%', '++', '--', '==', '!=', '||', '&&'];
    
    for (let i = 0; i < symbolCount; i++) {
      symbols.push({
        x: Math.random() * canvas.clientWidth,
        y: Math.random() * canvas.clientHeight,
        char: techChars[Math.floor(Math.random() * techChars.length)],
        color: 'rgba(255, 255, 255, 0.7)',
        size: Math.random() * 12 + 8,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.3 + 0.1
      });
    }

    // Digital wave
    interface WavePoint {
      x: number;
      y: number;
      originalY: number;
      amplitude: number;
      speed: number;
      phase: number;
    }

    const waves: WavePoint[][] = [];
    const waveCount = 3;
    const pointsPerWave = Math.ceil(canvas.clientWidth / 30);

    for (let w = 0; w < waveCount; w++) {
      const wave: WavePoint[] = [];
      const heightPosition = canvas.clientHeight * (0.3 + (w * 0.2));
      const amplitude = 5 + (w * 7);
      const waveSpeed = 0.01 + (w * 0.005);
      
      for (let i = 0; i <= pointsPerWave; i++) {
        const x = (canvas.clientWidth * i) / pointsPerWave;
        wave.push({
          x,
          y: heightPosition,
          originalY: heightPosition,
          amplitude,
          speed: waveSpeed,
          phase: Math.random() * Math.PI * 2
        });
      }
      
      waves.push(wave);
    }

    // Animation loop
    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      
      // Update and draw nodes
      for (const node of nodes) {
        // Move nodes
        node.x += node.vx;
        node.y += node.vy;
        
        // Bounce off edges
        if (node.x < 0 || node.x > canvas.clientWidth) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.clientHeight) node.vy *= -1;
        
        // Pulse effect
        node.pulse += 0.02 * node.pulseDirection;
        if (node.pulse > 1 || node.pulse < 0) node.pulseDirection *= -1;
        
        // Draw node
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size * (1 + node.pulse * 0.3), 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.fill();
        
        // Add glow effect
        const glowSize = node.size * (1.5 + node.pulse * 0.5);
        const gradient = ctx.createRadialGradient(
          node.x, node.y, node.size * 0.5,
          node.x, node.y, glowSize
        );
        gradient.addColorStop(0, node.color);
        gradient.addColorStop(1, node.color.replace('0.8', '0'));
        
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowSize, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
      }
      
      // Connect nodes with light lines
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          // Only connect nodes that are reasonably close
          if (distance < canvas.clientWidth * 0.2) {
            const opacity = (1 - distance / (canvas.clientWidth * 0.2)) * 0.5;
            
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            
            // Create a light gradient for the line
            const gradient = ctx.createLinearGradient(
              nodes[i].x, nodes[i].y, 
              nodes[j].x, nodes[j].y
            );
            gradient.addColorStop(0, nodes[i].color.replace('0.8', `${opacity}`));
            gradient.addColorStop(1, nodes[j].color.replace('0.8', `${opacity}`));
            
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 0.5 + (1 - distance / (canvas.clientWidth * 0.2)) * 1.5;
            ctx.stroke();
          }
        }
      }
      
      // Update and draw small floating dots
      for (const dot of dots) {
        dot.y -= dot.speed;
        if (dot.y < 0) {
          dot.y = canvas.clientHeight;
          dot.x = Math.random() * canvas.clientWidth;
        }
        
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2);
        ctx.fillStyle = dot.color.replace('0.8', `${dot.opacity}`);
        ctx.fill();
      }
      
      // Update and draw tech symbols
      ctx.font = 'bold 12px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      for (const symbol of symbols) {
        symbol.x += symbol.vx;
        symbol.y += symbol.vy;
        
        // Bounce off edges
        if (symbol.x < 0 || symbol.x > canvas.clientWidth) symbol.vx *= -1;
        if (symbol.y < 0 || symbol.y > canvas.clientHeight) symbol.vy *= -1;
        
        ctx.font = `bold ${symbol.size}px monospace`;
        ctx.fillStyle = `rgba(255, 255, 255, ${symbol.opacity})`;
        ctx.fillText(symbol.char, symbol.x, symbol.y);
      }
      
      // Update and draw digital waves
      for (let w = 0; w < waves.length; w++) {
        const wave = waves[w];
        const waveColor = colors[w % colors.length];
        
        // Update wave points
        for (const point of wave) {
          point.phase += point.speed;
          point.y = point.originalY + Math.sin(point.phase) * point.amplitude;
        }
        
        // Draw wave
        ctx.beginPath();
        ctx.moveTo(wave[0].x, wave[0].y);
        
        for (let i = 1; i < wave.length; i++) {
          const curr = wave[i];
          const prev = wave[i - 1];
          const xc = (prev.x + curr.x) / 2;
          const yc = (prev.y + curr.y) / 2;
          
          // Use quadratic curves for smoother waves
          ctx.quadraticCurveTo(prev.x, prev.y, xc, yc);
        }
        
        ctx.lineTo(wave[wave.length - 1].x, wave[wave.length - 1].y);
        ctx.lineTo(canvas.clientWidth, canvas.clientHeight);
        ctx.lineTo(0, canvas.clientHeight);
        ctx.closePath();
        
        // Create a gradient fill
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.clientHeight);
        gradient.addColorStop(0, waveColor.replace('0.8', '0.1'));
        gradient.addColorStop(1, waveColor.replace('0.8', '0'));
        
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Draw wave line
        ctx.beginPath();
        ctx.moveTo(wave[0].x, wave[0].y);
        
        for (let i = 1; i < wave.length; i++) {
          const curr = wave[i];
          const prev = wave[i - 1];
          const xc = (prev.x + curr.x) / 2;
          const yc = (prev.y + curr.y) / 2;
          
          ctx.quadraticCurveTo(prev.x, prev.y, xc, yc);
        }
        
        ctx.strokeStyle = waveColor.replace('0.8', '0.3');
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      
      requestAnimationFrame(animate);
    };
    
    // Start animation
    const animationId = requestAnimationFrame(animate);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-screen"
      style={{ opacity: 0.8 }}
    />
  );
};

export default HeroAnimation;